import { db } from "../../db/localDB";
import { getCatalogo, getTerritorios, getComunidadesDe } from "./bacSarampionAPI";

// Clave especial dentro de bac_catalogos_cache para guardar el mapa
// completo territorio → [comunidades] — no es un catálogo plano como
// los demás (es en cascada), por eso se guarda aparte con esta clave,
// aunque viva en la misma tabla.
const CLAVE_TERRITORIOS = "__territorios_comunidades__";

// Los 9 catálogos que necesita el formulario de visita. Si agregas un
// catálogo nuevo en el futuro, solo tienes que sumarlo aquí — el resto
// del mecanismo (descarga, caché, pantalla de carga) ya lo recoge solo.
export const TIPOS_CATALOGO = [
  "responsables-bac", "entrevistadores", "tipos-documento", "generos",
  "eps", "regimenes-afiliacion", "grupos-poblacionales", "etnias",
  "tipos-vivienda", "grupos-poblacionales-contacto",
];

// ── Leer lo que ya está cacheado (instantáneo, sin red) ─────────────────
export async function obtenerCatalogosCacheados() {
  const filas = await db.bac_catalogos_cache.toArray();
  const mapa = {};
  filas.forEach(f => { mapa[f.tipo] = f.datos; });
  return mapa;
}

// ── Territorio → Comunidades (lectura instantánea, sin red) ─────────────
// Devuelve { territorios: [...], porTerritorio: { "Leticia": [...], ... } }
export async function obtenerTerritoriosCacheados() {
  const fila = await db.bac_catalogos_cache.get(CLAVE_TERRITORIOS);
  return fila?.datos || { territorios: [], porTerritorio: {} };
}

export async function hayTerritoriosCacheados() {
  const { territorios, porTerritorio } = await obtenerTerritoriosCacheados();
  if (!territorios.length) return false;
  // Completo de verdad significa que CADA territorio tiene su lista de
  // comunidades ya guardada — no solo el nombre del territorio.
  return territorios.every(t => Array.isArray(porTerritorio[t]) && porTerritorio[t].length > 0);
}

// Descarga la lista de territorios y, para CADA UNO, sus comunidades —
// arma el mapa completo de una sola vez y lo cachea. Igual que con los
// demás catálogos: si algo falla a mitad de camino, lo que ya estaba
// cacheado de antes se queda tal cual, nunca se pisa con algo vacío.
export async function descargarYCachearTerritorios(timeoutMs = 20000) {
  try {
    const resTerritorios = await getTerritorios();
    const territorios = resTerritorios.data || [];
    if (!territorios.length) return { ok: false };

    const porTerritorio = {};
    const promesas = territorios.map(async (t) => {
      try {
        const res = await getComunidadesDe(t);
        porTerritorio[t] = res.data || [];
      } catch {
        porTerritorio[t] = [];
      }
    });

    await Promise.race([
      Promise.allSettled(promesas),
      new Promise(resolve => setTimeout(resolve, timeoutMs)),
    ]);

    // Solo se guarda si de verdad se consiguió al menos una comunidad
    // por territorio — si no, se deja el caché viejo como estaba, para
    // no reemplazar algo bueno por un intento fallido a medias.
    const completo = territorios.every(t => porTerritorio[t]?.length > 0);
    if (completo) {
      await db.bac_catalogos_cache.put({
        tipo: CLAVE_TERRITORIOS,
        datos: { territorios, porTerritorio },
        cachedAt: new Date().toISOString(),
      });
    }
    return { ok: completo };
  } catch {
    return { ok: false };
  }
}

// ── ¿Hay un caché completo y usable? (todos los catálogos, cada uno con
//    al menos una opción — un catálogo vacío no cuenta como "cacheado
//    de verdad", probablemente fue un intento fallido a medias) ────────
export async function hayCatalogosCompletos() {
  const filas = await db.bac_catalogos_cache
    .where("tipo").anyOf(TIPOS_CATALOGO).toArray();
  if (filas.length < TIPOS_CATALOGO.length) return false;
  if (!filas.every(f => Array.isArray(f.datos) && f.datos.length > 0)) return false;
  return hayTerritoriosCacheados();
}

// ── Descargar y cachear TODOS los catálogos, con límite de tiempo ──────
// Cada catálogo que SÍ llega a tiempo se guarda (pisa el caché viejo de
// ESE catálogo únicamente). Los que no llegan a tiempo o fallan se
// quedan tal cual estaban — nunca se borra un caché bueno por culpa de
// un intento fallido de refrescarlo.
//
// timeoutMs es un límite para dejar de ESPERAR (para no bloquear al
// usuario para siempre) — las descargas que ya estén en curso cuando se
// cumple el límite siguen su curso en segundo plano por si alcanzan a
// terminar solas y cachear igual, solo que ya no las esperamos.
export async function descargarYCachearCatalogos(timeoutMs = 20000) {
  let exitosos = 0;
  let fallidos = 0;

  const promesas = TIPOS_CATALOGO.map(async (tipo) => {
    try {
      const res = await getCatalogo(tipo);
      const datos = res.data || [];
      if (datos.length > 0) {
        await db.bac_catalogos_cache.put({ tipo, datos, cachedAt: new Date().toISOString() });
        exitosos++;
      } else {
        fallidos++; // el servidor respondió pero vacío — no pisamos un caché bueno con nada
      }
    } catch {
      fallidos++;
    }
  });

  // Territorios/comunidades van en el mismo tiempo límite, en paralelo
  // con el resto — así descargar todo junto no tarda más de lo que ya
  // tardaba antes.
  promesas.push(
    descargarYCachearTerritorios(timeoutMs).then(r => { r.ok ? exitosos++ : fallidos++; })
  );

  await Promise.race([
    Promise.allSettled(promesas),
    new Promise(resolve => setTimeout(resolve, timeoutMs)),
  ]);

  return { exitosos, fallidos, total: TIPOS_CATALOGO.length + 1 };
}